import {
  CLIENT_HEADER,
  CLIENT_NAME,
  CLIENT_NOT_ALLOWED,
  accessErrorCode,
} from '@src/utils/teamAccess';

// The backend origin (e.g. https://api.tilltech.xyz). The env var is
// REACT_APP_API_URL everywhere else — the old REACT_APP_API_BASE_URL was never
// defined, which made this `undefined` and degraded matching to a loose
// `url.includes('/api/')` that attached the session cookie to any URL merely
// containing "/api/" (including third-party ones).
const BASE_URL = process.env.REACT_APP_API_URL;

// localStorage key holding the id of the account the user is currently viewing
// (their own, or an owner who invited them). Kept in sync by AccountStore.
export const ACTIVE_ACCOUNT_STORAGE_KEY = 'gg.activeAccountId';

// sessionStorage key holding the id of the user an admin is impersonating.
// Deliberately sessionStorage (per-tab), NOT localStorage: impersonation is
// scoped to the single tab opened from the admin panel, so closing that tab
// ends it and the admin's other tabs / own session are untouched.
export const IMPERSONATE_STORAGE_KEY = 'gg.impersonateUserId';

/** The user id currently being impersonated in THIS tab, or null. */
export function getImpersonateUserId(): string | null {
  try {
    return window.sessionStorage.getItem(IMPERSONATE_STORAGE_KEY);
  } catch {
    return null;
  }
}

// Session expiry //////////////////////////////////////////////////////////////

// Set by index.tsx once the stores exist. Kept as a registered callback rather
// than a direct import so this module stays decoupled from MobX (see the
// active-account comment below) and free of an import cycle with AccountStore.
// Receives the URL of the request that 401'd, for analytics: knowing WHICH
// endpoint declared the session dead is what separates a real expiry from an
// endpoint answering 401 for its own reasons.
let sessionExpiredHandler: ((url: string) => void) | null = null;

// Fires at most once per page load. A dead session produces a BURST of 401s —
// six parallel /api/v1/fields calls on a dashboard load, plus the pollers that
// re-arm every few seconds — and each must not queue its own logout+redirect.
let sessionExpiredFired = false;

export function setSessionExpiredHandler(fn: (url: string) => void): void {
  sessionExpiredHandler = fn;
}

/**
 * Better Auth's own endpoints answer 401 in situations that are NOT an expired
 * session: a wrong password on the login screen, getSession() for a visitor who
 * was never logged in, and the sign-out call that the handler itself issues.
 * Treating those as expiry would log people out of the login page.
 */
function isAuthEndpoint(url: string): boolean {
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/api/auth/');
  } catch {
    return false;
  }
}

function onUnauthorized(url: string): void {
  if (sessionExpiredFired) return;
  sessionExpiredFired = true;
  sessionExpiredHandler?.(url);
}

// Client not allowed //////////////////////////////////////////////////////////

// A role can be limited to the driver app ("Can use: Driver app only"). Acting
// inside such an account from the web makes the backend answer 403
// CLIENT_NOT_ALLOWED to every call — typically because the owner changed the
// role while this tab was open. Registered by index.tsx (same decoupling as the
// session-expired handler); AccountStore falls back to the user's own account.
// The handler returns false when it leaves the case to someone else (AccountStore
// during boot, whose own 403 path already clears the account), so a refusal seen
// before boot finishes does not use up the once-per-page-load fallback.
let clientNotAllowedHandler: (() => boolean) | null = null;
let clientNotAllowedFired = false;

export function setClientNotAllowedHandler(fn: () => boolean): void {
  clientNotAllowedHandler = fn;
}

/** Read the 403 body from a clone, so the caller still gets an unread response. */
function checkClientNotAllowed(response: Response): void {
  if (clientNotAllowedFired) return;
  response
    .clone()
    .json()
    .then((body) => {
      if (accessErrorCode(body) !== CLIENT_NOT_ALLOWED || clientNotAllowedFired) return;
      if (clientNotAllowedHandler?.()) clientNotAllowedFired = true;
    })
    .catch(() => {
      /* not JSON — not ours */
    });
}

// HTTP ////////////////////////////////////////////////////////////////////////

const doHTTP = window.fetch.bind(window);

function isApiRequest(url: string): boolean {
  // Absolute calls to our configured backend origin.
  if (BASE_URL && url.startsWith(BASE_URL)) return true;
  // Same-origin API calls — covers relative "/api/..." and the dev fallback
  // where BASE_URL is unset and requests hit window.location.origin + "/api/...".
  // Deliberately excludes third-party URLs that merely contain "/api/".
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

async function HTTPInterceptor(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = (typeof input === 'string') ?
    input : ((input instanceof URL) ? input.toString() : input.url);

  if (!isApiRequest(url)) {
    return doHTTP(url, init);
  }

  // Admin impersonation takes precedence in this tab. When set, the backend
  // (gated on the admin's own session cookie) serves the impersonated user's
  // data. Per-tab sessionStorage, so it never leaks to the admin's other tabs.
  const impersonateUserId = getImpersonateUserId();

  // Active-account context for team access. When the user has switched to an
  // account they were invited to, the backend uses this header to resolve which
  // owner's data to serve (and with what scope). Read from localStorage so the
  // interceptor stays decoupled from the MobX stores and is always current.
  // Skipped while impersonating (impersonation supersedes team switching).
  let activeAccountId: string | null = null;
  if (!impersonateUserId) {
    try {
      activeAccountId = window.localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  const authenticatedInit: RequestInit = {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.headers as Record<string, string> | undefined),
      // Which client is calling (web / blue-android / blue-ios). The backend
      // uses it for the role's "Can use" rule inside another owner's account.
      // Sent on every API call, including while impersonating.
      [CLIENT_HEADER]: CLIENT_NAME,
      ...(impersonateUserId ? { 'X-Impersonate-User-Id': impersonateUserId } : {}),
      ...(activeAccountId ? { 'X-Active-Account-Id': activeAccountId } : {}),
    },
  };

  const response = await doHTTP(url, authenticatedInit);

  // Every backend call in the app funnels through here — including the ~20
  // services that call window.fetch directly and never touch services/api.ts —
  // so this is the only place that sees a session die no matter who noticed it.
  // The response is returned untouched; callers behave exactly as before.
  if (response.status === 401 && !isAuthEndpoint(url)) {
    onUnauthorized(url);
  }

  // Only a call made inside another account can be refused for the client.
  if (response.status === 403 && activeAccountId) {
    checkClientNotAllowed(response);
  }

  return response;
}

export const applyAuthInterceptor = () => window.fetch = HTTPInterceptor;
